import React, { useState, useEffect, useContext, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Tabs, Tab, Badge, Button, Chip,
  CircularProgress, Alert, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem as MuiMenuItem, Select, FormControl,
  InputLabel, Snackbar, Checkbox, FormControlLabel, Switch,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SendIcon from '@mui/icons-material/Send';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

function MessageCard({ msg, onMarkRead, onDelete, onRsvp, t }) {
  const [expanded, setExpanded] = useState(false);
  const isUnread = !msg.read;

  return (
    <Paper
      onClick={() => setExpanded(p => !p)}
      sx={{
        mb: 1.5, borderRadius: 2, border: `1px solid ${isUnread ? AMBER : '#e2e8f0'}`,
        background: isUnread ? '#fffbeb' : 'white',
        cursor: 'pointer', overflow: 'hidden', transition: 'all 0.15s',
        '&:hover': { borderColor: AMBER },
      }}
    >
      <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            {isUnread && <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: AMBER, flexShrink: 0 }} />}
            <Typography sx={{
              fontFamily: '"Outfit", sans-serif', fontWeight: isUnread ? 700 : 600,
              fontSize: '0.9rem', color: NAV_BG, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {msg.subject || msg.title || t('parent_messages_page.no_subject')}
            </Typography>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#94a3b8', ml: 'auto', flexShrink: 0 }}>
              {fmtTime(msg.created_at || msg.sent_at)}
            </Typography>
          </Box>
          <Typography sx={{
            fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#64748b',
            overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: expanded ? 'normal' : 'nowrap',
          }}>
            {msg.body || msg.text}
          </Typography>
          {expanded && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {isUnread && (
                <Button size="small" startIcon={<MarkEmailReadIcon sx={{ fontSize: 14 }} />}
                  onClick={e => { e.stopPropagation(); onMarkRead(msg); }}
                  sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', textTransform: 'none', color: '#1d4ed8', py: 0.3 }}>
                  {t('parent_messages_page.mark_read')}
                </Button>
              )}
              {msg.is_invitation && !msg.rsvp && (
                <>
                  <Button size="small" variant="contained" disableElevation
                    onClick={e => { e.stopPropagation(); onRsvp(msg, 'yes'); }}
                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', textTransform: 'none', background: '#10b981', color: 'white', py: 0.3 }}>
                    {t('parent_messages_page.rsvp_yes')}
                  </Button>
                  <Button size="small" variant="outlined"
                    onClick={e => { e.stopPropagation(); onRsvp(msg, 'no'); }}
                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', textTransform: 'none', borderColor: '#ef4444', color: '#ef4444', py: 0.3 }}>
                    {t('parent_messages_page.rsvp_no')}
                  </Button>
                </>
              )}
              {msg.rsvp && (
                <Chip size="small" label={t('parent_messages_page.rsvp_label', { response: msg.rsvp })} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', background: '#dcfce7', color: '#15803d' }} />
              )}
              <Button size="small" startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                onClick={e => { e.stopPropagation(); onDelete(msg); }}
                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', textTransform: 'none', color: '#ef4444', ml: 'auto', py: 0.3 }}>
                {t('common.delete')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function SentMessageCard({ msg, onDelete, t }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Paper
      onClick={() => setExpanded(p => !p)}
      sx={{
        mb: 1.5, borderRadius: 2, border: '1px solid #e2e8f0',
        background: 'white', cursor: 'pointer', overflow: 'hidden',
        transition: 'all 0.15s', '&:hover': { borderColor: AMBER },
      }}
    >
      <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography sx={{
              fontFamily: '"Outfit", sans-serif', fontWeight: 600,
              fontSize: '0.9rem', color: NAV_BG, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {msg.title || t('parent_messages_page.no_subject')}
            </Typography>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#94a3b8', ml: 'auto', flexShrink: 0 }}>
              {fmtTime(msg.sent_ts)}
            </Typography>
          </Box>
          <Typography sx={{
            fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#64748b',
            overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: expanded ? 'normal' : 'nowrap',
          }}>
            {msg.text}
          </Typography>
          {expanded && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                onClick={e => { e.stopPropagation(); onDelete(msg); }}
                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', textTransform: 'none', color: '#ef4444', ml: 'auto', py: 0.3 }}>
                {t('common.delete')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default function ParentMessagesPage() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const { t } = useTranslation();

  const [tab, setTab] = useState(0);
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [snack, setSnack] = useState(null);

  // Children state
  const [myChildren, setMyChildren] = useState([]);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  const hasChildren = myChildren.length > 0;

  // Compose state
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [sendTo, setSendTo] = useState('school');
  const [isInvitation, setIsInvitation] = useState(false);
  const [sending, setSending] = useState(false);

  // Classmate selection state
  const [classmates, setClassmates] = useState([]);
  const [classmatesLoading, setClassmatesLoading] = useState(false);
  const [selectedChildIds, setSelectedChildIds] = useState([]);

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  // Fetch parent's own children to determine if they have any school association
  useEffect(() => {
    fetch(backendUrl + 'api/parent/children/', { headers: authHeader })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setMyChildren(Array.isArray(data) ? data : []); setChildrenLoaded(true); })
      .catch(() => setChildrenLoaded(true));
  }, [backendUrl, authTokens]);

  const fetchInbox = useCallback(() => {
    fetch(backendUrl + 'api/messages/inbox/', { headers: authHeader })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const msgs = Array.isArray(data) ? data : [];
        setInbox(msgs);
        setUnreadCount(msgs.filter(m => !m.read).length);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [backendUrl, authTokens]);

  const fetchSent = useCallback(() => {
    fetch(backendUrl + 'api/parent/messages/sent/', { headers: authHeader })
      .then(r => r.ok ? r.json() : [])
      .then(data => setSent(Array.isArray(data) ? data : []))
      .catch(() => setSnack({ severity: 'error', msg: t('common.network_error') }));
  }, [backendUrl, authTokens]);

  useEffect(() => {
    fetchInbox();
    fetchSent();
    const interval = setInterval(fetchInbox, 30000);
    return () => clearInterval(interval);
  }, [fetchInbox, fetchSent]);

  // Load classmates when switching to the classmates option
  useEffect(() => {
    if (sendTo !== 'classmates' || !composeOpen) return;
    setClassmatesLoading(true);
    fetch(backendUrl + 'api/parent/school-children/', { headers: authHeader })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setClassmates(Array.isArray(data) ? data : []); setClassmatesLoading(false); })
      .catch(() => setClassmatesLoading(false));
  }, [sendTo, composeOpen]);

  const handleMarkRead = async (msg) => {
    try {
      const res = await fetch(backendUrl + `api/messages/${msg.message_id}/read/`, { method: 'POST', headers: authHeader });
      if (!res.ok) return;
      setInbox(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, read: true } : m));
      setUnreadCount(p => Math.max(0, p - 1));
      window.dispatchEvent(new Event('messages-read'));
    } catch { /* fire-and-forget */ }
  };

  const handleDelete = async (msg) => {
    try {
      const res = await fetch(backendUrl + 'api/parent/messages/delete/', {
        method: 'POST', headers: authHeader,
        body: JSON.stringify({ message_ids: [msg.message_id] }),
      });
      if (!res.ok) { setSnack({ severity: 'error', msg: t('common.network_error') }); return; }
      setInbox(prev => prev.filter(m => m.message_id !== msg.message_id));
      if (!msg.read) setUnreadCount(p => Math.max(0, p - 1));
    } catch { setSnack({ severity: 'error', msg: t('common.network_error') }); }
  };

  const handleDeleteSent = async (msg) => {
    try {
      const res = await fetch(backendUrl + 'api/parent/messages/delete-sent/', {
        method: 'POST', headers: authHeader,
        body: JSON.stringify({ group_ids: [msg.group_id] }),
      });
      if (!res.ok) { setSnack({ severity: 'error', msg: t('common.network_error') }); return; }
      setSent(prev => prev.filter(m => m.group_id !== msg.group_id));
    } catch { setSnack({ severity: 'error', msg: t('common.network_error') }); }
  };

  const handleRsvp = async (msg, rsvp) => {
    try {
      const res = await fetch(backendUrl + `api/messages/${msg.message_id}/rsvp/`, {
        method: 'POST', headers: authHeader,
        body: JSON.stringify({ rsvp }),
      });
      if (!res.ok) { setSnack({ severity: 'error', msg: t('common.network_error') }); return; }
      setInbox(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, rsvp } : m));
      setSnack({ severity: 'success', msg: t('parent_messages_page.rsvp_confirmed', { response: rsvp }) });
    } catch { setSnack({ severity: 'error', msg: t('common.network_error') }); }
  };

  const handleSend = async () => {
    if (!text.trim()) return;
    if (sendTo === 'classmates' && selectedChildIds.length === 0) return;
    setSending(true);
    try {
      const payload = {
        title,
        text,
        recipient_type: sendTo === 'classmates' ? 'children' : 'school',
        ...(sendTo === 'classmates' && { child_ids: selectedChildIds }),
        ...(sendTo === 'classmates' && isInvitation && { is_invitation: true }),
      };
      const res = await fetch(backendUrl + 'api/parent/send-message/', {
        method: 'POST', headers: authHeader,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSnack({ severity: 'success', msg: t('parent_messages_page.sent_success') });
        setComposeOpen(false);
        resetCompose();
        fetchSent();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ severity: 'error', msg: data.error || t('parent_messages_page.send_failed') });
      }
    } catch {
      setSnack({ severity: 'error', msg: t('common.network_error') });
    }
    setSending(false);
  };

  const resetCompose = () => {
    setTitle('');
    setText('');
    setSendTo('school');
    setIsInvitation(false);
    setSelectedChildIds([]);
    setClassmates([]);
  };

  const handleOpenCompose = () => {
    resetCompose();
    setComposeOpen(true);
  };

  const toggleChild = (childId) => {
    setSelectedChildIds(prev =>
      prev.includes(childId) ? prev.filter(id => id !== childId) : [...prev, childId]
    );
  };

  // Group classmates by class
  const classmatesByClass = classmates.reduce((acc, c) => {
    const key = c.class_name || c.department_name || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const canSend = text.trim() && (sendTo === 'school' || (sendTo === 'classmates' && selectedChildIds.length > 0));

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.4rem', color: NAV_BG }}>
            {t('nav.parent_messages')}
          </Typography>
        </Box>
        {childrenLoaded && hasChildren && (
          <Button
            variant="contained" disableElevation startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            onClick={handleOpenCompose}
            sx={{
              background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
              color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontWeight: 700,
              textTransform: 'none', borderRadius: 1.5,
            }}
          >
            {t('parent_messages_page.compose')}
          </Button>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTabs-indicator': { background: AMBER } }}>
        <Tab
          label={
            <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error"
              sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.9rem', textTransform: 'none' }}>
                {t('parent_messages_page.inbox')}
              </Typography>
            </Badge>
          }
          sx={{ textTransform: 'none', minWidth: 80 }}
        />
        <Tab
          label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.9rem', textTransform: 'none' }}>{t('parent_messages_page.sent_tab')}</Typography>}
          sx={{ textTransform: 'none', minWidth: 80 }}
        />
      </Tabs>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress sx={{ color: AMBER }} /></Box>}

      {!loading && tab === 0 && (
        inbox.length === 0
          ? <Alert severity="info" sx={{ borderRadius: 2 }}>{t('parent_messages_page.empty_inbox')}</Alert>
          : inbox.map(msg => (
              <MessageCard key={msg.message_id} msg={msg} onMarkRead={handleMarkRead} onDelete={handleDelete} onRsvp={handleRsvp} t={t} />
            ))
      )}

      {!loading && tab === 1 && (
        sent.length === 0
          ? <Alert severity="info" sx={{ borderRadius: 2 }}>{t('parent_messages_page.no_sent')}</Alert>
          : sent.map(msg => <SentMessageCard key={msg.group_id} msg={msg} onDelete={handleDeleteSent} t={t} />)
      )}

      {/* Compose dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
          {t('parent_messages_page.compose_title')}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel sx={{ fontFamily: '"Outfit", sans-serif' }}>{t('parent_messages_page.send_to_label')}</InputLabel>
            <Select value={sendTo} label={t('parent_messages_page.send_to_label')}
              onChange={e => { setSendTo(e.target.value); setSelectedChildIds([]); }}
              sx={{ fontFamily: '"Outfit", sans-serif', borderRadius: 1.5 }}>
              <MuiMenuItem value="school">{t('parent_messages_page.send_to_school')}</MuiMenuItem>
              <MuiMenuItem value="classmates">{t('parent_messages_page.send_to_classmates')}</MuiMenuItem>
            </Select>
          </FormControl>

          {/* Classmate selection */}
          {sendTo === 'classmates' && (
            <Box sx={{ mb: 2, border: '1px solid #e2e8f0', borderRadius: 1.5, maxHeight: 260, overflowY: 'auto' }}>
              {classmatesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={22} sx={{ color: AMBER }} />
                </Box>
              ) : classmates.length === 0 ? (
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>
                    {t('parent_messages_page.no_classmates')}
                  </Typography>
                </Box>
              ) : (
                Object.entries(classmatesByClass).map(([className, children]) => (
                  <Box key={className}>
                    <Box sx={{ px: 2, py: 0.75, background: '#f8fafc' }}>
                      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.78rem', color: '#64748b', letterSpacing: '0.04em' }}>
                        {className.toUpperCase()}
                      </Typography>
                    </Box>
                    {children.map(child => (
                      <Box key={child.child_id}
                        onClick={() => child.parent_reachable > 0 && toggleChild(child.child_id)}
                        sx={{
                          display: 'flex', alignItems: 'center', px: 2, py: 0.5,
                          cursor: child.parent_reachable > 0 ? 'pointer' : 'default',
                          opacity: child.parent_reachable === 0 ? 0.45 : 1,
                          '&:hover': child.parent_reachable > 0 ? { background: '#fffbeb' } : {},
                        }}>
                        <Checkbox
                          size="small" checked={selectedChildIds.includes(child.child_id)}
                          disabled={child.parent_reachable === 0}
                          onChange={() => toggleChild(child.child_id)}
                          onClick={e => e.stopPropagation()}
                          sx={{ color: AMBER, '&.Mui-checked': { color: AMBER_DARK }, p: 0.5 }}
                        />
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: NAV_BG, ml: 1, flex: 1 }}>
                          {child.firstname} {child.lastname}
                        </Typography>
                        {child.parent_reachable === 0 && (
                          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#94a3b8' }}>
                            {t('parent_messages_page.no_parent')}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </Box>
          )}

          {sendTo === 'classmates' && (
            <FormControlLabel
              control={
                <Switch
                  checked={isInvitation}
                  onChange={e => setIsInvitation(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER_DARK }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }}
                />
              }
              label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: NAV_BG }}>{t('parent_messages_page.request_rsvp')}</Typography>}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            fullWidth size="small" label={t('parent_messages_page.subject_label')} value={title} onChange={e => setTitle(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, '& label': { fontFamily: '"Outfit", sans-serif' }, '& input': { fontFamily: '"Outfit", sans-serif' } }}
          />
          <TextField
            fullWidth multiline rows={5} label={t('parent_messages_page.message_label')} value={text} onChange={e => setText(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, '& label': { fontFamily: '"Outfit", sans-serif' }, '& textarea': { fontFamily: '"Outfit", sans-serif' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setComposeOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button
            variant="contained" disableElevation onClick={handleSend}
            disabled={sending || !canSend}
            startIcon={sending ? <CircularProgress size={14} sx={{ color: NAV_BG }} /> : <SendIcon sx={{ fontSize: 16 }} />}
            sx={{
              background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
              color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontWeight: 700,
              textTransform: 'none', borderRadius: 1.5,
            }}
          >
            {t('parent_messages_page.send_btn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={20000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ top: { xs: 'calc(8px + var(--sat))', sm: 'calc(24px + var(--sat))' } }}>
        <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
